import { getFormat } from "./utils/formatContext";
import {v3} from "@alchemiesolns/maths";
import type {ElectronData} from "./svgElectrons";
import type {AtomContainer} from "@alchemiesolns/atomcontainer";
import { VisibilityConfigDef, VisibilityFlag } from "@alchemiesolns/visibility-config";

/**
 * Tracks lone pair indices per atom to generate unique IDs.
 * Must be reset at the start of each molecule render to ensure consistent indexing.
 */
const lonePairIndices = new Map<string, number>();

/**
 * Gets the next lone pair index for a given atom.
 * Indices are 1-based (1, 2, 3, 4) for user-friendliness.
 *
 * @param atomId - The parent atom's ID
 * @returns The next index for this atom's lone pairs (1-4)
 */
const getLonePairIndex = (atomId: string): number => {
    const currentIndex = lonePairIndices.get(atomId) || 0;
    const nextIndex = currentIndex + 1;
    lonePairIndices.set(atomId, nextIndex);
    return nextIndex;
};

/**
 * Resets the lone pair index tracking.
 * MUST be called at the start of each molecule render to ensure deterministic IDs.
 * Called automatically by ContainerSVG component.
 */
export const resetLonePairIndices = () => {
    lonePairIndices.clear();
};

export const svgElements = {
    /**
     * Renders an electron element (either a lone pair or radical).
     * Delegates to lonePair() or radical() based on electron type.
     *
     * @param electronData - Electron data containing position, direction, atomId, and type
     * @param atomContainer - Container with atoms array for index lookup
     * @param visDefs - Visibility configuration array
     * @returns JSX element wrapped in a <g> with required data attributes
     */
    electrons: (electronData: ElectronData, atomContainer: AtomContainer, visDefs: VisibilityConfigDef[]) => {
        if (electronData.type === "radical") return svgElements.radical(electronData, atomContainer, visDefs);
        else return svgElements.lonePair(electronData, atomContainer, visDefs);
    },
    /**
     * Renders a lone pair (two dots) wrapped in a group element with data attributes.
     * Adds required data attributes for drag-to-connect functionality:
     * - id: "{atomId}-lp{N}" where N is 1-4 (1-based index)
     * - data-atom-id: parent atom ID
     * - data-atom-index: parent atom index in container.atoms array
     * - data-lp-index: lone pair index as string ("1", "2", "3", or "4")
     * - data-visibility: "visible" | "hidden-lp" based on atom's HiddenLP flag
     *
     * All attributes follow the schema defined in specs/002-data-in-svg/data-model.md
     *
     * @param electronData - Electron data with atomId, position, and direction
     * @param atomContainer - Container with atoms array for index lookup
     * @param visDefs - Visibility configuration array
     * @returns JSX <g> element containing two <circle> elements
     */
    lonePair: (electronData: ElectronData, atomContainer: AtomContainer, visDefs: VisibilityConfigDef[]) => {
        const {pos, dir, atomId} = electronData;
        const perp = v3.crossVectors([0, 0, 1], v3.normalize(dir));
        const p0 = v3.addVectors(pos, v3.addScaledVector(dir, perp, getFormat().lonePair.ratios.gap/2));
        const p1 = v3.addVectors(pos, v3.addScaledVector(dir, perp, -getFormat().lonePair.ratios.gap/2));

        const lpIndex = getLonePairIndex(atomId);
        const id = `${atomId}-lp${lpIndex}`;

        // Look up atom index in container
        const atomIndex = atomContainer.atoms.findIndex(a => a.id === atomId);

        // Check if lone pairs are hidden for this atom
        const visDef = visDefs.find(v => v.a === atomIndex);
        const visibilityAttr = (visDef?.visibilityFlag === VisibilityFlag.HiddenLP ||
                                visDef?.visibilityFlag === VisibilityFlag.ForceTextHiddenLP)
            ? "hidden-lp" : "visible";

        return (
            <g
                key={id}
                id={id}
                className="svg-lonepair"
                data-atom-id={atomId}
                data-atom-index={String(atomIndex)}
                data-lp-index={String(lpIndex)}
                data-visibility={visibilityAttr}
            >
                <circle cx={p0[0]} cy={p0[1]} r={getFormat().lonePair.radius} stroke={"black"} fill={"black"} strokeWidth={0} vectorEffect="non-scaling-stroke"/>
                <circle cx={p1[0]} cy={p1[1]} r={getFormat().lonePair.radius} stroke={"black"} fill={"black"} strokeWidth={0} vectorEffect="non-scaling-stroke"/>
            </g>
        )
    },
    charge: (props: { chg: string; pos: number[] }, index: number) => {
        const { chg, pos } = props;
        const fontSize = getFormat().charge.multiFont;
        const radius = getFormat().charge.radius ;
        const strokeWidth = getFormat().charge.outlineScale;

        // For + and -, use custom SVG shapes
        if (chg === "+") {
            const lineLength = radius * getFormat().charge.symbol.length;
            const lineWidth = strokeWidth * getFormat().charge.symbol.width;
            return (
                <g key={`charge-${index}`} className="svg-charge">
                    <circle
                        cx={pos[0]}
                        cy={pos[1]}
                        r={radius}
                        stroke={"black"}
                        fill={"white"}
                        strokeWidth={strokeWidth}
                    />
                    {/* Horizontal line */}
                    <rect
                        x={pos[0] - lineLength / 2}
                        y={pos[1] - lineWidth / 2}
                        width={lineLength}
                        height={lineWidth}
                        fill={"black"}
                    />
                    {/* Vertical line */}
                    <rect
                        x={pos[0] - lineWidth / 2}
                        y={pos[1] - lineLength / 2}
                        width={lineWidth}
                        height={lineLength}
                        fill={"black"}
                    />
                </g>
            );
        } else if (chg === "_") {
            const lineLength = radius * getFormat().charge.symbol.length;
            const lineWidth = strokeWidth * getFormat().charge.symbol.width;
            return (
                <g key={`charge-${index}`} className="svg-charge">
                    <circle
                        cx={pos[0]}
                        cy={pos[1]}
                        r={radius}
                        stroke={"black"}
                        fill={"white"}
                        strokeWidth={strokeWidth}
                    />
                    {/* Horizontal line */}
                    <rect
                        x={pos[0] - lineLength / 2}
                        y={pos[1] - lineWidth / 2}
                        width={lineLength}
                        height={lineWidth}
                        fill={"black"}
                    />
                </g>
            );
        }

        // For other charges (numbers), use text
        return (
            <g key={`charge-${index}`} className="svg-charge">
                <circle
                    cx={pos[0]}
                    cy={pos[1]}
                    r={radius}
                    stroke={"black"}
                    fill={"white"}
                    strokeWidth={strokeWidth}
                />
                <text
                    x={pos[0]}
                    y={pos[1]}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={fontSize}
                    stroke={"black"}
                    fill="black"
                    strokeWidth={chg === "_" ? ".15px" : "0px"}
                >
                    {chg}
                </text>
            </g>
        );
    },
    /**
     * Renders a radical (single unpaired electron dot) wrapped in a group element.
     * Adds the same data attributes as lone pairs for consistent handling:
     * - id: "{atomId}-lp{N}" format (uses "lp" prefix for consistency)
     * - data-atom-id: parent atom ID
     * - data-atom-index: parent atom index in container.atoms array
     * - data-lp-index: electron index as string
     * - data-visibility: always "visible" (radicals don't use HiddenLP flag)
     *
     * Note: Radicals use className "svg-lonepair" for unified drag-to-connect handling.
     * The visual difference (1 circle vs 2) is internal to the rendering.
     *
     * @param electronData - Electron data with atomId, position, and direction
     * @param atomContainer - Container with atoms array for index lookup
     * @param visDefs - Visibility configuration array (unused for radicals, but kept for consistency)
     * @returns JSX <g> element containing a single <circle> element
     */
    radical: (electronData: ElectronData, atomContainer: AtomContainer, _visDefs: VisibilityConfigDef[]) => {
        const {pos, dir, atomId} = electronData;
        const p = v3.addVectors(pos, dir);

        const lpIndex = getLonePairIndex(atomId);
        const id = `${atomId}-lp${lpIndex}`;

        // Look up atom index in container
        const atomIndex = atomContainer.atoms.findIndex(a => a.id === atomId);

        // Radicals are always visible (not affected by HiddenLP flag)
        const visibilityAttr = "visible";

        return (
            <g
                key={id}
                id={id}
                className="svg-lonepair"
                data-atom-id={atomId}
                data-atom-index={String(atomIndex)}
                data-lp-index={String(lpIndex)}
                data-visibility={visibilityAttr}
            >
                <circle cx={p[0]} cy={p[1]} r={getFormat().lonePair.radius} stroke={"black"} fill={"black"} strokeWidth={0} vectorEffect="non-scaling-stroke"/>
            </g>
        )
    }
}